import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit } from '@angular/core';

import {
  EmployeeApprovalDocument,
  PrivateDocumentUploadListResponse
} from '../../models/employeeDocument';
import {
  addUploadedDocuments,
  removeDocument
} from '../../helpers/employee-document.helper';
import {
  Department,
  DropdownOption
} from 'src/app/kuba/businesses/models/businessDepartment';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { EmployeePrivateDocumentServices } from '../../services/employeeprivatedocument.service';
import { Subscription } from 'rxjs';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { TranslateService } from '@ngx-translate/core';
import { UploaderConfig } from 'src/app/shared/ecpl-document-explorer/uploader-config';
import { environment } from 'src/environments/environment';
import { AppErrorCode, FeatureKey, Roles, Status } from 'src/app/_models';
import { EmployeeServices } from '../../services/employee.services';
import { HelperService } from 'src/app/_services/helper.service';
import { getFileExtension } from 'src/app/kuba/shared/helpers/file.helper';

@Component({
  selector: 'approval-document',
  templateUrl: './approval-document.component.html',
  styleUrls: ['./approval-document.component.scss'],
  standalone: false
})
export class ApprovalDocumentComponent implements OnInit {
  private readonly notifications = inject(NotificationService);
  approvalDocumentList: EmployeeApprovalDocument[] = [];
  originalApprovalDocumentList: EmployeeApprovalDocument[] = [];
  selectedEmployees: EmployeeApprovalDocument[] = [];
  selectedEmployeesForApproval: EmployeeApprovalDocument[] = [];

  private currentFileSearch = '';
  uploadedFiles: any = [];
  isPortal = false;
  departmentOptions: DropdownOption[];
  typeList: DropdownOption[] = [];
  rowId: number | null;
  isCollapsed = false;
  loading = false;
  newDocumentAdded: any = [];
  private subscriptions: Subscription[] = [];
  docId: number;
  showSavingLoader = false;
  files: any = [];
  selectedDocumentsForApprovalNotice: any[];

  additionalData = {
    ApplicationId: BaseServices.ApplicationId,
    BusinessId: BaseServices.BusinessId,
    EmployeeId: 0,
    IsPrivateDocument: true,
    Status: Status.Active,
    CreatedBy: BaseServices.UserId,
    CreatedOn: '',
    ModifiedBy: BaseServices.UserId,
    ModifiedOn: '',
    IsDocumentedTraining: false
  };

  uploadConfig: UploaderConfig = {
    title: null!,
    titleAsLabel: true,
    editMode: true,
    windowedHeight: false,
    viewSwitch: false,
    showUserMeta: false,
    showSearchFilter: false,
    IsPrivateUploadButton: true,
    showDeleteButton: true,
    uploaderUri:
      environment.BASE_URL + '/file/upload/business/employee/private-docs',
    addtionalData: this.additionalData,
    pdEmpId: null
  };

  constructor(
    private route: ActivatedRoute,
    private employeePrivateDocumentServices: EmployeePrivateDocumentServices,
    private employeeServices: EmployeeServices,
    private translate: TranslateService
  ) {
    this.approvalDocumentList = this.route.snapshot.data['approvalDocuments'];
    this.originalApprovalDocumentList =
      this.route.snapshot.data['approvalDocuments'];

    this.subscriptions.push(
      this.translate.stream('SELECT_DROPDOWN').subscribe(val => {
        this.typeList = [];
        this.typeList.push({ label: val.SELECT, value: null });
        var types = this.approvalDocumentList
          .map((item: any) => item.TypeOfEmployee)
          .filter(
            (value: any, index: any, self: any) => self.indexOf(value) === index
          );
        types.forEach((element: any) => {
          this.typeList.push({ label: element, value: element });
        });
      })
    );
  }

  /**
   * On initialize
   */
  ngOnInit(): void {
    this.setupDepartmentsDropdown();

    this.isPortal = Number(BaseServices.roleId) === Roles.PORTAL;

    if (this.isPortal) {
      const portalUploadData = {
        ApplicationId: BaseServices.ApplicationId,
        PortalId: BaseServices.PortalId
      };

      this.uploadConfig.uploaderUri =
        environment.BASE_URL + '/file/upload/portal/employee/private-docs';

      this.uploadConfig.addtionalData = portalUploadData;
    }
  }

  /**
   * Setup departments dropdown
   */
  setupDepartmentsDropdown(): void {
    const departmentsFromRoute: Department[] =
      this.route.snapshot.data['busDepartment'];

    this.subscriptions.push(
      this.translate.stream('SELECT_DROPDOWN').subscribe(val => {
        this.departmentOptions = [];
        this.departmentOptions.push({ label: val.SELECT, value: null });
      })
    );

    if (departmentsFromRoute) {
      departmentsFromRoute.forEach((department: Department) => {
        this.departmentOptions.push({
          label: department.Name,
          value: department.Name
        });
      });
    }
  }

  /**
   * On table header checkbox toggle
   */
  onTableHeaderCheckboxToggle() {
    this.selectedEmployeesForApproval = this.approvalDocumentList;
  }

  /**
   * Filter results based on input data
   * @param data filter data
   */
  filterResults(data: any) {
    this.currentFileSearch = data || '';
    if (data == null || data == '') {
      this.approvalDocumentList = this.originalApprovalDocumentList;
    } else {
      var arr: any = [];
      this.originalApprovalDocumentList.forEach((x: any) => {
        var filter = [];
        if (x.EmployeeDocuments != null && x.EmployeeDocuments.length > 0) {
          x.EmployeeDocuments.forEach((ele: any) => {
            if (
              ele.Filename != null &&
              ele.Filename.toLowerCase().includes(data.toLowerCase())
            )
              filter.push(ele);
          });
          if (filter.length > 0) arr.push(x);
        }
      });
      this.approvalDocumentList = arr;
    }
  }

  /**
   * Save uploaded files
   * @param event Event data
   */
  saveUploaded(event: any) {
    if (event) {
      let eventType = event.eventName;
      switch (eventType) {
        case 'DeleteDefaultFile':
          if (event.item.data.id === 0) {
            // remove unsaved attachment
            this.newDocumentAdded = this.newDocumentAdded.filter(
              (x: any) => x.Path !== event.item.data.path
            );
            this.uploadedFiles = this.uploadedFiles.filter(
              (x: any) => x.path !== event.item.data.path
            );
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
          } else {
            // remove existing attachement
            this.subscriptions.push(
              this.employeeServices
                .deleteEmployeeDocument(event.item.data.id)
                .subscribe((result: any) => {
                  if (result) {
                    this.newDocumentAdded = this.newDocumentAdded.filter(
                      (x: any) => x.Path !== event.item.data.path
                    );
                    // remove from display list
                    this.uploadedFiles = this.uploadedFiles.filter(
                      (x: any) => x.path !== event.item.data.path
                    );
                    this.notifications.success('UI_NOTIFICATIONS.DELETED');
                  }
                })
            );
          }
          break;
        case 'remove':
          this.uploadedFiles = this.uploadedFiles.filter(
            (x: any) => x.id !== +event.id
          );
          break;
        case 'ShowOfficeFileSaveloader':
          this.subscriptions.push(
            this.employeePrivateDocumentServices
              .updateDoc(event.item.id, 0, false)
              .subscribe(res => {
                this.docId = event.item.id;
                if (res) {
                  this.subscriptions.push(
                    this.employeePrivateDocumentServices
                      .list(0, 1)
                      .subscribe(result => {
                        if (result.length > 0) {
                          this.uploadedFiles = result;
                          this.saveApprovalDocuments();
                        }
                      })
                  );
                }
              })
          );
          if (parseInt(sessionStorage.getItem('isDocumentChanged')!, null!)) {
            this.showSavingLoader = true;
            setTimeout(() => {
              this.showSavingLoader = false;
              this.newDocumentAdded = [];
            }, 15000);
            sessionStorage.setItem('isDocumentChanged', '0');
          }
          break;
        default:
          let keyBindings = {
            Originalname: 'OriginalFileName',
            FileTypeId: 'FileType'
          };
          let privateDoc = Object.assign({}, event, this.additionalData);
          privateDoc.id = 0;
          privateDoc.status = '1';
          privateDoc.createdBy = BaseServices.UserId;
          this.newDocumentAdded.push(
            HelperService.switchKeysCase(privateDoc, keyBindings)
          );

          break;
      }
    }
  }

  /**
   * Map files for upload
   */
  mapFilesForUpload() {
    this.newDocumentAdded.forEach((element: any) => {
      this.selectedEmployeesForApproval.forEach((x: any) => {
        var tempDoc: any = {};
        tempDoc.BusinessId = element.BusinessId;
        tempDoc.CreatedBy = element.CreatedBy;
        tempDoc.CreatedOn = element.CreatedOn;
        tempDoc.EmployeeId = x.EmployeeId;
        tempDoc.Extension = element.Extension;
        tempDoc.FileType = element.FileType;
        tempDoc.Filename = element.Filename;
        tempDoc.Id = element.Id;
        tempDoc.IsPrivateDocument = element.IsPrivateDocument;
        tempDoc.Mimetype = element.Mimetype;
        tempDoc.ModifiedBy = element.ModifiedBy;
        tempDoc.ModifiedOn = element.ModifiedOn;
        tempDoc.OriginalFileName = element.OriginalFileName;
        tempDoc.Path = element.Path;
        tempDoc.Size = element.Size;
        tempDoc.Status = element.Status;
        tempDoc.IsDocumentedTraining = false;
        this.files.push(tempDoc);
      });
    });
  }

  /**
   * Save uploaded files for the selected employees
   */
  saveDocuments() {
    if (
      this.newDocumentAdded.length > 0 &&
      this.selectedEmployeesForApproval.length > 0
    ) {
      this.loading = true;
      this.mapFilesForUpload();
      this.subscriptions.push(
        this.employeePrivateDocumentServices.saveDocs(this.files).subscribe({
          next: result => {
            if (result) {
              this.notifications.success('UI_NOTIFICATIONS.SAVED');
              this.loading = false;
              this.newDocumentAdded = [];
              this.addSavedDocuments(result);
              this.selectedEmployeesForApproval = [];
              this.uploadedFiles = [];
              this.files = [];
            }
          },
          error: () => {
            this.loading = false;
            this.files = [];
          }
        })
      );
    }
  }

  /**
   * Adds the saved documents to their employees, so the grid shows them without reloading the
   * whole list, and re-applies the filename search so a filtered view includes them too.
   * @param savedDocuments The saved documents, one entry per document and employee
   */
  private addSavedDocuments(
    savedDocuments: PrivateDocumentUploadListResponse[]
  ): void {
    addUploadedDocuments(
      this.originalApprovalDocumentList,
      savedDocuments,
      BaseServices.Name
    );
    this.filterResults(this.currentFileSearch);
  }

  /**
   * Returns true if the employee has documents requiring approval
   */
  hasDocumentsForApproval = (emp: any) =>
    emp.TotalApprovedDocuments < emp.TotalDocuments;

  /**
   * Save approval documents and send approval notice
   * @param saveMode Save mode (1: send for approval, 2: approve documents)
   */
  saveApprovalDocuments() {
    if (this.newDocumentAdded.length > 0) {
      // Files uploaded — save docs first, then notify all selected employees
      this.loading = true;
      this.mapFilesForUpload();
      this.subscriptions.push(
        this.employeePrivateDocumentServices.saveDocs(this.files).subscribe({
          next: result => {
            if (result) {
              this.notifications.success('UI_NOTIFICATIONS.SAVED');
              this.newDocumentAdded = [];
              // The documents are stored whether or not the notice below succeeds.
              this.addSavedDocuments(result);
              this.files = [];
              const employeeIds = this.selectedEmployeesForApproval.map(
                (emp: any) => ({ employeeId: emp.EmployeeId })
              );
              this.subscriptions.push(
                this.employeePrivateDocumentServices
                  .sendApprovalRequest(employeeIds)
                  .subscribe({
                    next: () => {
                      this.notifications.success(
                        'UI_NOTIFICATIONS.APPROVAL_SENT'
                      );
                      this.loading = false;
                      this.selectedEmployeesForApproval = [];
                      this.uploadedFiles = [];
                    },
                    error: (err: HttpErrorResponse) => {
                      this.loading = false;
                      if (err.error?.Code === AppErrorCode.UserLacksEmail)
                        this.showMissingEmailToast(err.error.Detail);
                      else this.showGenericErrorToast(err.status);
                    }
                  })
              );
            }
          },
          error: () => {
            this.loading = false;
            this.files = [];
          }
        })
      );
    } else {
      // No files — notify only selected employees that have unapproved documents
      this.loading = true;
      const employeeIds = this.selectedEmployeesForApproval
        .filter((emp: any) => emp.TotalApprovedDocuments < emp.TotalDocuments)
        .map((emp: any) => ({ employeeId: emp.EmployeeId }));
      this.subscriptions.push(
        this.employeePrivateDocumentServices
          .sendApprovalRequest(employeeIds)
          .subscribe({
            next: () => {
              this.notifications.success('UI_NOTIFICATIONS.APPROVAL_SENT');
              this.loading = false;
              this.selectedEmployeesForApproval = [];
            },
            error: (err: HttpErrorResponse) => {
              this.loading = false;
              if (err.error?.Code === AppErrorCode.UserLacksEmail)
                this.showMissingEmailToast(err.error.Detail);
              else this.showGenericErrorToast(err.status);
            }
          })
      );
    }
  }

  /**
   * Set collapse state for a row
   * @param i Index of the row
   * @returns True if the row is collapsed, false otherwise
   */
  setCollapse(i: number): boolean {
    let isCollapsed = i === this.rowId;
    return !isCollapsed;
  }

  /**
   * Toggle click for a row
   * @param i Index of the row
   */
  toggleClick(i: number): void {
    this.rowId = i;
    this.isCollapsed = !this.isCollapsed;
  }

  /**
   * Toggle click for a row
   * @param i Index of the row
   */
  toggleClickHide(i: number): void {
    this.rowId = null;
  }

  /**
   * Get file extension
   * @param filename Name of the file
   * @returns File extension
   */
  getFileExtension(filename: string): string {
    return getFileExtension(filename);
  }

  /**
   * Deletes a document
   * @param documentId Id of the document to delete
   */
  deleteDoc(documentId: number) {
    this.loading = true;
    this.subscriptions.push(
      this.employeePrivateDocumentServices.delete(documentId).subscribe({
        next: () => {
          this.loading = false;
          this.notifications.success('UI_NOTIFICATIONS.DELETED');
          removeDocument(this.originalApprovalDocumentList, documentId);
          // An employee whose only matching document was deleted leaves the filtered view.
          this.filterResults(this.currentFileSearch);
        },
        error: () => {
          this.loading = false;
        }
      })
    );
  }

  /**
   * On destroy, unsubscribe all subscriptions
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }

  private showMissingEmailToast(detail: string): void {
    const lang = sessionStorage.getItem('languageMode');
    const summary =
      lang === 'no-NO'
        ? 'Manglende e-postadresse'
        : lang === 'sv-SE'
          ? 'Saknar e-postadress'
          : 'Missing email address';
    this.notifications.add({
      severity: 'warn',
      summary: summary,
      detail: detail
    });
  }

  private showGenericErrorToast(status: number): void {
    this.notifications.add({
      severity: 'error',
      summary: this.translate.instant('GENERAL_ERROR.UNEXPECTED_ERROR_TITLE', {
        status
      }),
      detail: this.translate.instant('GENERAL_ERROR.UNEXPECTED_ERROR_MESSAGE', {
        status,
        technicalSupport: this.translate.instant(
          'PLEASE_CONTACT_TECHNICAL_TEAM'
        )
      })
    });
  }
}
